'use client'

// Third-party
import { useState } from 'react'
import { motion } from 'framer-motion'
import { Mail, Phone, MapPin, Send, CheckCircle, AlertCircle, MessageCircle } from 'lucide-react'

// First-party
import { sendContactEmail as sendEmail } from '../utils/emailService'
import { ContactFormData as FormData } from '../interfaces/ContactFormData'

const contactInfo = [
  {
    icon: Phone,
    title: "Teléfono",
    info: "+52 56 6778 3855",
    detail: "Lun - Vie, 9:00 AM - 6:00 PM",
    color: "from-blue-400 to-cyan-500"
  },
  {
    icon: Mail,
    title: "Email",
    info: "contacto@ligarivieraflag.com",
    detail: "Respuesta en 24 horas",
    color: "from-green-400 to-emerald-500"
  },
  {
    icon: MapPin,
    title: "Oficina",
    info: "Cancún, Quintana Roo",
    detail: "Zona Hotelera, México",
    color: "from-purple-400 to-pink-500"
  }
]

const asuntos = [
  "Información General",
  "Registro de Equipo",
  "Patrocinios",
  "Medios de Comunicación",
  "Soporte Técnico",
  "Otro"
]

export default function ContactForm() {
  const [formData, setFormData] = useState<FormData>({
    nombre: '',
    email: '',
    telefono: '',
    asunto: '',
    mensaje: ''
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.nombre.trim()) {
      newErrors.nombre = 'El nombre es requerido'
    }

    if (!formData.email.trim()) {
      newErrors.email = 'El email es requerido'
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'El email no es válido'
    }

    if (!formData.telefono.trim()) {
      newErrors.telefono = 'El teléfono es requerido'
    }

    if (!formData.asunto.trim()) {
      newErrors.asunto = 'Debe seleccionar un asunto'
    }

    if (!formData.mensaje.trim()) {
      newErrors.mensaje = 'El mensaje es requerido'
    } else if (formData.mensaje.trim().length < 10) {
      newErrors.mensaje = 'El mensaje debe tener al menos 10 caracteres'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) return

    setIsSubmitting(true)

    // Simular envío del formulario
    setTimeout(() => {
      setIsSubmitting(false)
      setIsSubmitted(true)
    }, 2000)

    sendEmail({subject: 'Nuevo mensaje de contacto', title: 'Nuevo Mensaje', formData });
  }

  const resetForm = () => {
    setIsSubmitted(false)
    setFormData({
      nombre: '',
      email: '',
      telefono: '',
      asunto: '',
      mensaje: ''
    })
    setErrors({})
  }

  if (isSubmitted) {
    return (
      <section className="py-20 bg-gradient-to-b from-black via-gray-900 to-black">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-2xl mx-auto text-center"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              className="w-24 h-24 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-8"
              animate={{
                scale: [1, 1.1, 1],
                rotate: [0, 360]
              }}
              transition={{
                duration: 2,
                ease: "easeInOut"
              }}
            >
              <CheckCircle className="w-12 h-12 text-white" />
            </motion.div>
            
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
              ¡MENSAJE <span className="text-yellow-400">ENVIADO</span>!
            </h2>
            
            <p className="text-xl text-gray-300 mb-8">
              Hemos recibido tu mensaje. Nuestro equipo se pondrá en contacto contigo dentro de las próximas 24 horas.
            </p>
            
            <motion.button
              onClick={resetForm}
              className="bg-gradient-to-r from-yellow-400 to-orange-500 text-black px-8 py-3 rounded-full font-bold text-lg hover:shadow-2xl transition-all duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              ENVIAR OTRO MENSAJE
            </motion.button>
          </motion.div>
        </div>
      </section>
    )
  }

  return (
    <section className="py-20 bg-gradient-to-b from-black via-gray-900 to-black relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <motion.div
          className="absolute inset-0 opacity-20"
          animate={{
            background: [
              'radial-gradient(circle at 20% 30%, rgba(255, 196, 0, 0.1) 0%, transparent 70%)',
              'radial-gradient(circle at 80% 70%, rgba(255, 196, 0, 0.1) 0%, transparent 70%)',
              'radial-gradient(circle at 20% 30%, rgba(255, 196, 0, 0.1) 0%, transparent 70%)'
            ]
          }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <motion.h2 
            className="text-5xl md:text-6xl font-black text-white mb-6"
            style={{ fontWeight: 900 }}
          >
            <MessageCircle className="inline w-16 h-16 text-yellow-400 mr-4 mb-2" />
            CONTACTO
          </motion.h2>
          <div className="h-1 w-32 bg-gradient-to-r from-yellow-400 to-orange-500 mx-auto mb-6" />
          <motion.p 
            className="text-xl text-gray-300 max-w-3xl mx-auto"
            style={{ fontWeight: 400 }}
          >
            ¿Tienes preguntas? Nuestro equipo está listo para ayudarte. Contáctanos y te responderemos pronto.
          </motion.p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-12">
          {/* Contact Information */}
          <motion.div
            className="space-y-8"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h3 className="text-3xl font-black text-white mb-8">
              Información de <span className="text-yellow-400">Contacto</span>
            </h3>
            
            {contactInfo.map((item, index) => {
              const IconComponent = item.icon
              
              return (
                <motion.div
                  key={item.title}
                  className="bg-gradient-to-br from-gray-800/50 to-gray-900/80 rounded-xl p-6 backdrop-blur-sm border border-gray-700/50 hover:border-yellow-400/50 transition-all duration-300"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="flex items-start space-x-4">
                    <div className={`w-14 h-14 bg-gradient-to-br ${item.color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                      <IconComponent className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h4 className="text-white font-bold text-lg mb-1">{item.title}</h4>
                      <p className="text-yellow-400 font-semibold mb-1">{item.info}</p>
                      <p className="text-gray-400 text-sm">{item.detail}</p>
                    </div>
                  </div>
                </motion.div>
              )
            })}

            {/* Social Media */}
            <motion.div
              className="bg-gradient-to-br from-yellow-400/10 to-orange-500/10 rounded-xl p-6 border border-yellow-400/20"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4, duration: 0.6 }}
            >
              <h4 className="text-white font-bold text-lg mb-4">Síguenos en Redes</h4>
              <div className="flex space-x-4">
                {['📘', '📷', '🐦', '📺'].map((icon, index) => (
                  <motion.button
                    key={index}
                    className="w-12 h-12 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-yellow-400 transition-colors duration-300"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <span className="text-xl">{icon}</span>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            className="lg:col-span-2"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <form
              onSubmit={handleSubmit}
              className="bg-gradient-to-br from-gray-800/50 to-gray-900/80 rounded-2xl p-8 backdrop-blur-sm border border-gray-700/50"
            >
              <h3 className="text-3xl font-black text-white mb-8">
                Envíanos un <span className="text-yellow-400">Mensaje</span>
              </h3>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                {/* Nombre */}
                <div className="space-y-2">
                  <label className="text-white font-semibold">Nombre Completo</label>
                  <input
                    type="text"
                    value={formData.nombre}
                    onChange={(e) => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
                    className="w-full bg-black/50 border border-gray-600 rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/20 transition-all duration-300"
                    placeholder="Tu nombre completo"
                  />
                  {errors.nombre && (
                    <p className="text-red-400 text-sm flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.nombre}
                    </p>
                  )}
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <label className="text-white font-semibold">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full bg-black/50 border border-gray-600 rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/20 transition-all duration-300"
                    placeholder="tu@email.com"
                  />
                  {errors.email && (
                    <p className="text-red-400 text-sm flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.email}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                {/* Teléfono */}
                <div className="space-y-2">
                  <label className="text-white font-semibold">Teléfono</label>
                  <input
                    type="tel"
                    value={formData.telefono}
                    onChange={(e) => setFormData(prev => ({ ...prev, telefono: e.target.value }))}
                    className="w-full bg-black/50 border border-gray-600 rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/20 transition-all duration-300"
                    placeholder="+52 998 123 4567"
                  />
                  {errors.telefono && (
                    <p className="text-red-400 text-sm flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.telefono}
                    </p>
                  )}
                </div>

                {/* Asunto */}
                <div className="space-y-2">
                  <label className="text-white font-semibold">Asunto</label>
                  <select
                    value={formData.asunto}
                    onChange={(e) => setFormData(prev => ({ ...prev, asunto: e.target.value }))}
                    className="w-full bg-black/50 border border-gray-600 rounded-xl px-4 py-3 text-white focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/20 transition-all duration-300"
                  >
                    <option value="">Selecciona un asunto</option>
                    {asuntos.map((asunto) => (
                      <option key={asunto} value={asunto}>{asunto}</option>
                    ))}
                  </select>
                  {errors.asunto && (
                    <p className="text-red-400 text-sm flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.asunto}
                    </p>
                  )}
                </div>
              </div>

              {/* Mensaje */}
              <div className="space-y-2 mb-8">
                <label className="text-white font-semibold">Mensaje</label>
                <textarea
                  value={formData.mensaje}
                  onChange={(e) => setFormData(prev => ({ ...prev, mensaje: e.target.value }))}
                  rows={6}
                  className="w-full bg-black/50 border border-gray-600 rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/20 transition-all duration-300 resize-none"
                  placeholder="Escribe tu mensaje aquí..."
                />
                {errors.mensaje && (
                  <p className="text-red-400 text-sm flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.mensaje}
                  </p>
                )}
                <p className="text-gray-400 text-sm">
                  Caracteres: {formData.mensaje.length} / Mínimo 10
                </p>
              </div>

              {/* Submit Button */}
              <motion.button
                type="submit"
                disabled={isSubmitting}
                className={`w-full bg-gradient-to-r from-yellow-400 to-orange-500 text-black py-4 rounded-xl text-xl font-bold transition-all duration-300 ${
                  isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:shadow-2xl'
                }`}
                whileHover={!isSubmitting ? { 
                  scale: 1.02,
                  boxShadow: '0 20px 40px rgba(255, 196, 0, 0.4)'
                } : {}}
                whileTap={!isSubmitting ? { scale: 0.98 } : {}}
                style={{ fontWeight: 700 }}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <motion.div
                      className="w-6 h-6 border-2 border-black border-t-transparent rounded-full mr-3"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    />
                    ENVIANDO MENSAJE...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <Send className="w-6 h-6 mr-3" />
                    ENVIAR MENSAJE
                  </span>
                )}
              </motion.button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  )
}